import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
  KEYWORD_HISTORY_LIMIT,
  pushKeywordHistory,
  readKeywordHistory,
  removeKeywordHistory,
} from './keyword-history.logic';

/**
 * @element kd-keyword-suggest
 * @description ヘッダー検索フォームの検索履歴サジェスト。
 *
 * - submit 時に input.value を localStorage の履歴に保存
 * - input focus 時に履歴があれば dropdown を表示
 * - dropdown のアイテムクリックで input にキーワードを反映し form を submit
 * - dropdown の削除ボタンクリックで該当履歴を削除（dropdown は開いたまま）
 *
 * 必要な子要素（Liquid 側で配置）:
 *   - form
 *   - input[data-keyword-suggest-input]
 *   - [data-keyword-suggest-dropdown]（初期は hidden）
 *     - [data-keyword-suggest-list]（li の親）
 *     - [data-keyword-suggest-empty]（履歴が0件のときに表示するメッセージ。任意）
 *
 * dropdown 内のアイテム HTML は本コンポーネントが組み立てる（テンプレート要素方式は採らず、
 * シンプルな DOM 構築でカバー範囲を最小化する）。
 */
@customElement('kd-keyword-suggest')
export class KdKeywordSuggest extends LitElement {
  createRenderRoot() {
    return this;
  }

  private input: HTMLInputElement | null = null;

  private form: HTMLFormElement | null = null;

  private dropdown: HTMLElement | null = null;

  private list: HTMLElement | null = null;

  private empty: HTMLElement | null = null;

  private submitIcon: HTMLElement | null = null;

  private submitSpinner: HTMLElement | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.input = this.querySelector<HTMLInputElement>('[data-keyword-suggest-input]');
    this.form = this.querySelector<HTMLFormElement>('form') ?? this.input?.closest('form') ?? null;
    this.dropdown = this.querySelector<HTMLElement>('[data-keyword-suggest-dropdown]');
    this.list = this.querySelector<HTMLElement>('[data-keyword-suggest-list]');
    this.empty = this.querySelector<HTMLElement>('[data-keyword-suggest-empty]');
    this.submitIcon = this.querySelector<HTMLElement>('[data-keyword-suggest-icon]');
    this.submitSpinner = this.querySelector<HTMLElement>('[data-keyword-suggest-spinner]');

    if (!this.input || !this.dropdown || !this.list) return;

    this.input.addEventListener('focus', this.handleFocus);
    this.input.addEventListener('blur', this.handleBlur);
    this.form?.addEventListener('submit', this.handleSubmit);
    // dropdown 全体: mousedown で input フォーカスを維持 → click で確定
    this.dropdown.addEventListener('mousedown', this.handleDropdownMouseDown);
    this.dropdown.addEventListener('click', this.handleDropdownClick);
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.input?.removeEventListener('focus', this.handleFocus);
    this.input?.removeEventListener('blur', this.handleBlur);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.dropdown?.removeEventListener('mousedown', this.handleDropdownMouseDown);
    this.dropdown?.removeEventListener('click', this.handleDropdownClick);
    document.removeEventListener('click', this.handleDocumentClick);
  }

  private handleSubmit = (): void => {
    const value = this.input?.value ?? '';
    pushKeywordHistory(value, KEYWORD_HISTORY_LIMIT);
    // submit はそのまま進む（preventDefault しない）
  };

  private handleFocus = (): void => {
    this.resetLoading();
    this.renderItems();
    this.open();
  };

  private handleBlur = (): void => {
    this.close();
  };

  private handleDropdownMouseDown = (e: MouseEvent): void => {
    // フォーカス移動を防ぐことで input の blur を発生させない。
    // blur → rAF → renderItems() が mousedown と click の間に割り込み、
    // クリック対象の DOM を replaceChildren() で破棄してしまう問題を回避する。
    e.preventDefault();
  };

  private handleDropdownClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const removeBtn = target.closest<HTMLElement>('[data-keyword-suggest-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const keyword = removeBtn.dataset.keyword ?? '';
      if (keyword) {
        removeKeywordHistory(keyword);
        this.renderItems();
        // 0件になったら閉じる
        if (readKeywordHistory().length === 0) {
          this.close();
        }
      }
      return;
    }

    const item = target.closest<HTMLElement>('[data-keyword-suggest-item]');
    if (item && this.input && this.form) {
      const keyword = item.dataset.keyword ?? '';
      this.input.value = keyword;
      this.close();
      // requestSubmit() は submit イベントを発火し、handleSubmit 経由で
      // クリックしたキーワードを履歴の先頭に再配置できる。
      // 未対応ブラウザ向けフォールバックとして直接 pushKeywordHistory + submit を実行する。
      this.setLoading();
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        pushKeywordHistory(keyword, KEYWORD_HISTORY_LIMIT);
        this.form.submit();
      }
    }
  };

  private handleDocumentClick = (e: MouseEvent): void => {
    if (!this.dropdown || !this.input) return;
    const target = e.target as Node | null;
    if (!target) return;
    if (this.contains(target)) return;
    this.close();
  };

  private open(): void {
    if (!this.dropdown) return;
    if (readKeywordHistory().length === 0) return;
    this.dropdown.hidden = false;
  }

  private setLoading(): void {
    if (this.submitIcon) this.submitIcon.hidden = true;
    if (this.submitSpinner) this.submitSpinner.hidden = false;
  }

  private resetLoading(): void {
    if (this.submitSpinner) this.submitSpinner.hidden = true;
    if (this.submitIcon) this.submitIcon.hidden = false;
  }

  private close(): void {
    if (!this.dropdown) return;
    this.dropdown.hidden = true;
  }

  private renderItems(): void {
    if (!this.list) return;
    const items = readKeywordHistory();

    if (this.empty) {
      this.empty.hidden = items.length > 0;
    }

    this.list.replaceChildren(...items.map((keyword, index) => this.buildItem(keyword, index, items.length)));
  }

  private buildItem(keyword: string, index: number, total: number): HTMLElement {
    const li = document.createElement('li');
    const isLast = index === total - 1;
    // 行全体をクリックターゲットにする（padding エリアの dead zone を解消）
    li.dataset.keywordSuggestItem = '';
    li.dataset.keyword = keyword;
    li.className = [
      'tw:flex tw:items-center tw:gap-3 tw:px-5 tw:py-2.5 tw:min-h-[44px]',
      'tw:bg-surface-main tw:border tw:border-border-default',
      index > 0 ? 'tw:border-t-0' : '',
      isLast ? 'tw:rounded-b' : '',
    ].filter(Boolean).join(' ');

    const textBtn = document.createElement('button');
    textBtn.type = 'button';
    textBtn.className = 'tw:flex-1 tw:min-w-0 tw:text-left tw:text-s tw:tracking-[1.4px] tw:text-text-main tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:break-words';
    textBtn.textContent = keyword;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.dataset.keywordSuggestRemove = '';
    removeBtn.dataset.keyword = keyword;
    removeBtn.setAttribute('aria-label', `「${keyword}」を履歴から削除`);
    removeBtn.className = 'tw:shrink-0 tw:inline-flex tw:items-center tw:justify-center tw:w-3 tw:h-3 tw:text-status-sale tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer';
    removeBtn.innerHTML = '<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="tw:w-3 tw:h-3"><rect width="24.1203" height="1.20601" transform="matrix(0.710629 0.703567 -0.710629 0.703567 0.859375 0.180664)" fill="currentColor"/><rect width="24.1203" height="1.20601" transform="matrix(-0.710629 0.703567 0.710629 0.703567 17.1406 0)" fill="currentColor"/></svg>';

    li.appendChild(textBtn);
    li.appendChild(removeBtn);
    return li;
  }
}
